import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT, COLORS } from '../config';
import { achievementManager, CATEGORY_DISPLAY_NAMES } from '../managers/AchievementManager';
import { formatNumber } from '../utils/formatNumber';

type MenuTab = 'main' | 'settings';

export class InGameMenu {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private overlay: Phaser.GameObjects.Graphics;
  private isVisible: boolean = false;
  private currentTab: MenuTab = 'main';
  private onResume: () => void;
  private onMainMenu: () => void;

  // Settings state
  private musicVolume: number = 0.7;
  private sfxVolume: number = 0.7;

  constructor(scene: Phaser.Scene, onResume: () => void, onMainMenu: () => void) {
    this.scene = scene;
    this.onResume = onResume;
    this.onMainMenu = onMainMenu;

    // Dark overlay
    this.overlay = scene.add.graphics();
    this.overlay.setDepth(3000);
    this.overlay.setVisible(false);

    // Container for all menu elements
    this.container = scene.add.container(0, 0);
    this.container.setDepth(3001);
    this.container.setVisible(false);
  }

  show(): void {
    this.isVisible = true;
    this.currentTab = 'main';
    this.overlay.setVisible(true);
    this.container.setVisible(true);
    this.render();
  }

  hide(): void {
    this.isVisible = false;
    this.overlay.setVisible(false);
    this.container.setVisible(false);
    this.clearContainer();
    this.overlay.clear();
  }

  getIsVisible(): boolean {
    return this.isVisible;
  }

  private clearContainer(): void {
    this.container.removeAll(true);
  }

  private render(): void {
    this.clearContainer();

    // Draw overlay
    this.overlay.clear();
    this.overlay.fillStyle(0x000000, 0.6);
    this.overlay.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    // Make overlay interactive to block clicks through
    const overlayHit = this.scene.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT)
      .setInteractive()
      .setAlpha(0.001);
    this.container.add(overlayHit);

    switch (this.currentTab) {
      case 'main': this.renderMainMenu(); break;
      case 'settings': this.renderSettings(); break;
    }
  }

  private renderMainMenu(): void {
    const panelW = 300;
    const panelH = 320;
    const panelX = (GAME_WIDTH - panelW) / 2;
    const panelY = (GAME_HEIGHT - panelH) / 2;

    // Panel background
    const panelBg = this.scene.add.graphics();
    panelBg.fillStyle(0x1a1a2e, 0.97);
    panelBg.fillRoundedRect(panelX, panelY, panelW, panelH, 12);
    panelBg.lineStyle(2, 0x4488ff, 0.6);
    panelBg.strokeRoundedRect(panelX, panelY, panelW, panelH, 12);
    this.container.add(panelBg);

    // Title
    const title = this.scene.add.text(GAME_WIDTH / 2, panelY + 30, 'PAUSED', {
      fontSize: '28px',
      fontFamily: 'Arial Black, Arial, sans-serif',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    this.container.add(title);

    // Menu buttons
    const btnW = 220;
    const btnH = 44;
    const btnX = (GAME_WIDTH - btnW) / 2;
    const startY = panelY + 90; // Moved down slightly since one button less
    const gap = 60;

    const buttons = [
      { label: 'Resume', action: () => { this.hide(); this.onResume(); } },
      { label: 'Settings', action: () => { this.currentTab = 'settings'; this.render(); } },
      { label: 'Main Menu', action: () => { this.hide(); this.onMainMenu(); } },
    ];

    buttons.forEach((btn, i) => {
      const y = startY + i * gap;
      const isMainMenu = btn.label === 'Main Menu';
      const color = isMainMenu ? 0x7a2d2d : COLORS.buttonNormal;
      const hoverColor = isMainMenu ? 0x9a3d3d : COLORS.buttonHover;
      this.createMenuButton(btnX, y, btnW, btnH, btn.label, color, hoverColor, btn.action);
    });
  }

  private renderSettings(): void {
    const panelW = 360;
    const panelH = 280;
    const panelX = (GAME_WIDTH - panelW) / 2;
    const panelY = (GAME_HEIGHT - panelH) / 2;

    // Panel background
    const panelBg = this.scene.add.graphics();
    panelBg.fillStyle(0x1a1a2e, 0.97);
    panelBg.fillRoundedRect(panelX, panelY, panelW, panelH, 12);
    panelBg.lineStyle(2, 0x4488ff, 0.6);
    panelBg.strokeRoundedRect(panelX, panelY, panelW, panelH, 12);
    this.container.add(panelBg);

    // Title
    const title = this.scene.add.text(GAME_WIDTH / 2, panelY + 25, 'Settings', {
      fontSize: '20px',
      fontFamily: 'Arial Black, Arial, sans-serif',
      color: '#ffffff',
    }).setOrigin(0.5);
    this.container.add(title);

    // Settings entries
    const settingsStartY = panelY + 60;
    const labelX = panelX + 30;

    // Music Volume
    this.createSliderSetting(labelX, settingsStartY, 'Music Volume', this.musicVolume, (val) => {
      this.musicVolume = val;
    });

    // SFX Volume
    this.createSliderSetting(labelX, settingsStartY + 60, 'SFX Volume', this.sfxVolume, (val) => {
      this.sfxVolume = val;
    });

    // Info text
    const infoText = this.scene.add.text(GAME_WIDTH / 2, settingsStartY + 140, 'Audio coming soon!', {
      fontSize: '11px',
      fontFamily: 'Arial, sans-serif',
      color: '#666666',
      fontStyle: 'italic',
    }).setOrigin(0.5);
    this.container.add(infoText);

    // Back button
    this.createMenuButton(
      (GAME_WIDTH - 160) / 2, panelY + panelH - 50, 160, 36,
      'Back', COLORS.buttonNormal, COLORS.buttonHover,
      () => { this.currentTab = 'main'; this.render(); }
    );
  }

  private createSliderSetting(x: number, y: number, label: string, value: number, onChange: (val: number) => void): void {
    const labelText = this.scene.add.text(x, y, label, {
      fontSize: '13px',
      fontFamily: 'Arial, sans-serif',
      color: '#cccccc',
    });
    this.container.add(labelText);

    const sliderW = 200;
    const sliderH = 8;
    const sliderX = x;
    const sliderY = y + 24;

    // Slider track
    const trackGfx = this.scene.add.graphics();
    trackGfx.fillStyle(0x333333, 1);
    trackGfx.fillRoundedRect(sliderX, sliderY, sliderW, sliderH, 4);
    this.container.add(trackGfx);

    // Slider fill
    const fillGfx = this.scene.add.graphics();
    fillGfx.fillStyle(0x4488ff, 1);
    fillGfx.fillRoundedRect(sliderX, sliderY, sliderW * value, sliderH, 4);
    this.container.add(fillGfx);

    // Value text
    const valText = this.scene.add.text(sliderX + sliderW + 10, sliderY + sliderH / 2, `${Math.round(value * 100)}%`, {
      fontSize: '11px',
      fontFamily: 'Arial, sans-serif',
      color: '#aaaaaa',
    }).setOrigin(0, 0.5);
    this.container.add(valText);

    // Clickable area for slider
    const sliderHit = this.scene.add.rectangle(sliderX + sliderW / 2, sliderY + sliderH / 2, sliderW, sliderH + 16)
      .setInteractive({ useHandCursor: true })
      .setAlpha(0.001);
    this.container.add(sliderHit);

    sliderHit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const localX = pointer.x - sliderX;
      const newVal = Math.max(0, Math.min(1, localX / sliderW));
      onChange(newVal);
      fillGfx.clear();
      fillGfx.fillStyle(0x4488ff, 1);
      fillGfx.fillRoundedRect(sliderX, sliderY, sliderW * newVal, sliderH, 4);
      valText.setText(`${Math.round(newVal * 100)}%`);
    });
  }

  private createMenuButton(x: number, y: number, w: number, h: number, label: string, color: number, hoverColor: number, action: () => void): void {
    const bg = this.scene.add.graphics();
    bg.fillStyle(color, 1);
    bg.fillRoundedRect(x, y, w, h, 8);
    bg.lineStyle(1, 0xffffff, 0.15);
    bg.strokeRoundedRect(x, y, w, h, 8);
    this.container.add(bg);

    const text = this.scene.add.text(x + w / 2, y + h / 2, label, {
      fontSize: '15px',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      color: '#ffffff',
      padding: { top: 3, bottom: 3 },
    }).setOrigin(0.5);
    this.container.add(text);

    const hitArea = this.scene.add.rectangle(x + w / 2, y + h / 2, w, h)
      .setInteractive({ useHandCursor: true })
      .setAlpha(0.001);
    this.container.add(hitArea);

    hitArea.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(hoverColor, 1);
      bg.fillRoundedRect(x, y, w, h, 8);
      bg.lineStyle(1, 0xffffff, 0.25);
      bg.strokeRoundedRect(x, y, w, h, 8);
    });
    hitArea.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(color, 1);
      bg.fillRoundedRect(x, y, w, h, 8);
      bg.lineStyle(1, 0xffffff, 0.15);
      bg.strokeRoundedRect(x, y, w, h, 8);
    });
    hitArea.on('pointerdown', action);
  }
}
